import { useTranslation } from 'react-i18next';
import { Loader2, CircleCheck, OctagonX } from 'lucide-react';

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { FieldGroup, FieldSet } from '@/components/ui/field';

import { useGetUserContextQuery } from '@/entities/layout';
import { FormInput } from '@/shared/ui/form';

import { useChangePasswordForm } from '../model';

interface ChangePasswordModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ChangePasswordModal = ({ open, onOpenChange }: ChangePasswordModalProps) => {
  const { t } = useTranslation();
  const { data: userContext } = useGetUserContextQuery();

  const { form, onSubmit, alertState, isLoading } = useChangePasswordForm({ open });

  const {
    register,
    handleSubmit,
    formState: { errors, isValid, isDirty }
  } = form;

  const user = userContext?.user;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px]">
        <DialogHeader>
          <DialogTitle>{t('password.title', 'Change Password')}</DialogTitle>
        </DialogHeader>

        {alertState && (
          <Alert variant={alertState.type === 'error' ? 'destructive' : 'success'} className="mb-4">
            {alertState.type === 'error' ? <OctagonX className="h-4 w-4" /> : <CircleCheck className="h-4 w-4" />}
            <AlertTitle>{alertState.type === 'error' ? 'Error' : 'Success'}</AlertTitle>
            <AlertDescription>{alertState.message}</AlertDescription>
          </Alert>
        )}

        {/* Read-only profile fields matching expectations */}
        <FieldSet>
          <FieldGroup className="grid grid-cols-1 md:grid-cols-3 gap-4 pb-4 border-b border-border mb-4">
            <FormInput label={t('user.org.label')} value={user?.organizationName} readOnly />{' '}
            <FormInput label={t('user.email.label', 'Email')} value={user?.email} readOnly />
            <FormInput label={t('user.name.label', 'Name')} value={user?.name} readOnly />
          </FieldGroup>
        </FieldSet>

        <form onSubmit={handleSubmit(onSubmit)}>
          <FieldGroup>
            <FormInput
              id="oldPassword"
              type="password"
              label={t('password.old', 'Old password')}
              placeholder={t('password.old', 'Old password')}
              registration={register('oldPassword')}
              error={errors.oldPassword?.message}
              required
            />

            <FormInput
              id="newPassword"
              type="password"
              label={t('password.new', 'New password')}
              placeholder={t('password.new', 'New password')}
              registration={register('newPassword')}
              error={errors.newPassword?.message}
              required
            />

            <FormInput
              id="retypePassword"
              type="password"
              label={t('password.retype', 'Retype')}
              placeholder={t('password.retype', 'Retype')}
              registration={register('retypePassword')}
              error={errors.retypePassword?.message}
              required
            />
          </FieldGroup>

          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.closeDialog', 'Close Dialog')}
            </Button>
            <Button type="submit" disabled={isLoading || !isDirty || !isValid}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('common.saveChanges', 'Save Changes')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
